<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { formatTime } from '../constants';
import { useToggle } from '@vueuse/core';

import MetricCard from './overview/MetricCard.vue';
import FilterChip from './Filters/FilterChip.vue';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import {
  findListAttribute,
  TYPE_ATTRIBUTE_HINT,
  STAGE_ATTRIBUTE_HINT,
} from 'dashboard/components-next/Contacts/contactStageColors';
import ContactStagesTable from './overview/ContactStagesTable.vue';
import OverviewKpiRow from './overview/OverviewKpiRow.vue';
import LeadSourcesCard from './overview/LeadSourcesCard.vue';
import AttentionCard from './overview/AttentionCard.vue';
import HeatmapDateRangeSelector from './heatmaps/HeatmapDateRangeSelector.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import getUnixTime from 'date-fns/getUnixTime';
import format from 'date-fns/format';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
const { t, locale } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const stagesI18nKey = 'OVERVIEW_REPORTS.CONTACT_STAGES';

// Single Date Range
const selectedFrom = ref(null);
const selectedTo = ref(null);

// Criteria selection (default: contact_created)
const selectedCriteria = ref('contact_created');

const criteriaOptions = computed(() => [
  {
    label: t(`${stagesI18nKey}.CRITERIA_COHORT`),
    value: 'contact_created',
  },
  {
    label: t(`${stagesI18nKey}.CRITERIA_ACTIVITY`),
    value: 'activity',
  },
]);

const selectedCriteriaOption = computed(() =>
  criteriaOptions.value.find(option => option.value === selectedCriteria.value)
);

const selectedCriteriaLabel = computed(() => {
  return selectedCriteria.value === 'contact_created'
    ? t(`${stagesI18nKey}.CRITERIA_COHORT_SHORT`)
    : t(`${stagesI18nKey}.CRITERIA_ACTIVITY_SHORT`);
});

const selectedCriteriaTooltip = computed(() => {
  return selectedCriteria.value === 'contact_created'
    ? t(`${stagesI18nKey}.CRITERIA_COHORT_TOOLTIP`)
    : t(`${stagesI18nKey}.CRITERIA_ACTIVITY_TOOLTIP`);
});

// Equipo y fuente: el endpoint ya los aceptaba (team_id, utm_source) pero
// no había forma de elegirlos desde la pantalla.
const teams = useMapGetter('teams/getTeams');
const selectedTeam = ref(null);

// Un filtro por cada atributo de contacto marcado para sincronizar con
// etiquetas —Tipo, Estado, Marca y los que añada la cuenta—, igual que en
// Bandeja y Contactos. El valor elegido viaja como contact_attributes[clave].
const contactAttributeDefinitions = useMapGetter(
  'attributes/getContactAttributes'
);
const excludedFilterIds = computed(() =>
  [
    findListAttribute(
      contactAttributeDefinitions.value,
      'estado_contacto',
      STAGE_ATTRIBUTE_HINT
    ),
    findListAttribute(
      contactAttributeDefinitions.value,
      'tipo_contacto',
      TYPE_ATTRIBUTE_HINT
    ),
  ]
    .filter(Boolean)
    .map(attribute => attribute.id)
);

const syncedAttributes = computed(() =>
  syncedListAttributes(contactAttributeDefinitions.value).filter(
    attribute => !excludedFilterIds.value.includes(attribute.id)
  )
);
const selectedContactAttributes = ref({});

const attributeOptions = attribute =>
  (attribute.attributeValues || []).map(value => ({ id: value, name: value }));

const attributeSelection = attribute =>
  selectedContactAttributes.value[attribute.attributeKey] || null;

const setAttributeSelection = (attribute, option) => {
  const next = { ...selectedContactAttributes.value };
  if (option) {
    next[attribute.attributeKey] = option;
  } else {
    delete next[attribute.attributeKey];
  }
  selectedContactAttributes.value = next;
};

const stagesTable = computed(() => {
  return accountConversationMetric.value?.contact_stages_table || [];
});

const cohortSummary = computed(() => {
  return (
    accountConversationMetric.value?.cohort_summary || {
      total_contacts: 0,
      total_won: 0,
      conversion_rate: 0,
      avg_won_time_seconds: 0,
    }
  );
});

const neverRespondedCount = computed(() => {
  return accountConversationMetric.value?.never_responded ?? 0;
});

const attention = computed(
  () => accountConversationMetric.value?.attention || {}
);

const utmSources = computed(() => {
  return accountConversationMetric.value?.utm_sources || [];
});

// Leads unicos por fuente: es el denominador correcto de la conversion por fuente
const utmTotals = computed(() => {
  return accountConversationMetric.value?.utm_totals || {};
});

// Monto cerrado por fuente. Vacio mientras la cuenta no designe el atributo de monto.
const utmRevenue = computed(() => {
  return accountConversationMetric.value?.utm_revenue || {};
});

// Al filtrar por una fuente, la respuesta ya sólo trae esa: se guarda la
// lista completa de la última consulta sin filtrar para no quedarse con una
// sola opción en el desplegable.
const activeView = ref('funnel');

const viewOptions = computed(() => [
  {
    value: 'table',
    icon: 'i-lucide-table-2',
    label: t(`${stagesI18nKey}.VIEW_TABLE`),
  },
  {
    value: 'funnel',
    icon: 'i-lucide-filter',
    label: t(`${stagesI18nKey}.VIEW_FUNNEL`),
  },
]);

const showUtmBreakdown = ref(
  localStorage.getItem('chatwoot_overview_utm_breakdown') === 'true'
);

watch(showUtmBreakdown, val => {
  try {
    localStorage.setItem('chatwoot_overview_utm_breakdown', String(val));
  } catch {
    // Ignore
  }
});

// El desglose por fuentes sólo tiene sentido en la tabla: al encenderlo
// desde el embudo se cambia de vista en vez de dejar el mando sin efecto.
const toggleUtmBreakdown = () => {
  showUtmBreakdown.value = !showUtmBreakdown.value;
  if (showUtmBreakdown.value) activeView.value = 'table';
};

const currentFilterParams = computed(() => {
  const params = {};
  if (selectedTeam.value) params.team_id = selectedTeam.value.id;
  const attributes = Object.entries(selectedContactAttributes.value).reduce(
    (result, [key, option]) => ({ ...result, [key]: option.id }),
    {}
  );
  if (Object.keys(attributes).length) params.contact_attributes = attributes;
  if (selectedFrom.value && selectedTo.value) {
    if (selectedCriteria.value === 'contact_created') {
      params.contact_created_since = getUnixTime(selectedFrom.value);
      params.contact_created_until = getUnixTime(selectedTo.value);
    } else {
      params.since = getUnixTime(selectedFrom.value);
      params.until = getUnixTime(selectedTo.value);
    }
  }
  return params;
});

const fetchData = () => {
  store.dispatch('fetchAccountConversationMetric', currentFilterParams.value);
};

const { startRefetching } = useLiveRefresh(fetchData);

const onCriteriaChange = option => {
  selectedCriteria.value = option.value;
};

watch(
  [
    selectedFrom,
    selectedTo,
    selectedCriteria,
    selectedTeam,
    selectedContactAttributes,
  ],
  () => {
    fetchData();
  }
);

// --- Exportacion del reporte ---
const reportRef = ref(null);
const isExporting = ref(false);
const [showExportDropdown, toggleExportDropdown] = useToggle();

const exportOptions = computed(() => [
  {
    label: t(`${stagesI18nKey}.EXPORT_PDF`),
    value: 'pdf',
    action: 'export_pdf',
  },
  {
    label: t(`${stagesI18nKey}.EXPORT_PNG`),
    value: 'png',
    action: 'export_png',
  },
]);

const exportContext = computed(() => {
  const parts = [
    `${t(`${stagesI18nKey}.FILTER_CRITERIA_LABEL`)}: ${selectedCriteriaLabel.value}`,
  ];
  if (selectedFrom.value && selectedTo.value) {
    parts.push(
      `${t(`${stagesI18nKey}.DATE_FILTER_LABEL`)}: ${format(selectedFrom.value, 'dd MMM yyyy')} - ${format(selectedTo.value, 'dd MMM yyyy')}`
    );
  }
  parts.push(
    `${t(`${stagesI18nKey}.EXPORT_GENERATED_AT`)}: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`
  );
  return parts.join('  ·  ');
});

const exportSummary = computed(() => {
  const summary = cohortSummary.value;
  return [
    `${t(`${stagesI18nKey}.SUMMARY_TOTAL_LEADS`)}: ${summary.total_contacts}`,
    `${t(`${stagesI18nKey}.SUMMARY_WON_SALES`)}: ${summary.total_won}`,
    `${t(`${stagesI18nKey}.SUMMARY_GLOBAL_CONVERSION`)}: ${summary.conversion_rate}%`,
  ].join('   ·   ');
});

const moneyFormatter = computed(
  () =>
    new Intl.NumberFormat(locale.value || 'es', { maximumFractionDigits: 2 })
);

const formatMoney = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return '-';
  return moneyFormatter.value.format(amount);
};

const formatDuration = seconds =>
  !seconds || seconds <= 0 ? '-' : formatTime(seconds);

const wonRow = computed(
  () => stagesTable.value.find(row => row.is_won_stage) || null
);

// Conversion por fuente: ganados sobre leads unicos de esa fuente, el mismo
// denominador que muestra la fila de totales
const conversionBySource = computed(() => {
  const result = {};
  utmSources.value.forEach(source => {
    const total = utmTotals.value[source] ?? 0;
    result[source] = total
      ? Math.round(((wonRow.value?.utm_counts?.[source] || 0) / total) * 1000) /
        10
      : null;
  });
  return result;
});

// ---------------------------------------------------------------------------
// PDF
//
// Se genera el documento desde los datos en vez de imprimir el DOM. Imprimir
// obligaba a pelear contra el layout de la app (scroll, columnas sticky, tema
// oscuro) y el resultado dependia del driver de impresora que eligiera el
// usuario. Aca sale siempre igual, con texto vectorial y paginacion propia.
// ---------------------------------------------------------------------------
const exportPdf = async () => {
  if (isExporting.value) return;

  isExporting.value = true;
  try {
    const { generateContactStagesPdf, buildFunnelLayersForPdf } =
      await import('../helper/contactStagesPdf');

    const summary = cohortSummary.value;
    const summaryCards = [
      {
        label: t(`${stagesI18nKey}.SUMMARY_TOTAL_LEADS`),
        value: summary.total_contacts,
      },
      {
        label: t(`${stagesI18nKey}.SUMMARY_WON_SALES`),
        value: summary.total_won,
      },
      {
        label: t(`${stagesI18nKey}.SUMMARY_GLOBAL_CONVERSION`),
        value: `${summary.conversion_rate}%`,
      },
      {
        label: t(`${stagesI18nKey}.SUMMARY_AVG_CLOSING`),
        value: formatDuration(summary.avg_won_time_seconds),
      },
    ];

    if (summary.has_deal_amount) {
      summaryCards.push(
        {
          label: t(`${stagesI18nKey}.SUMMARY_REVENUE`),
          value: formatMoney(summary.total_revenue),
        },
        {
          label: t(`${stagesI18nKey}.SUMMARY_AVG_TICKET`),
          value: formatMoney(summary.avg_ticket),
        }
      );
    }

    await generateContactStagesPdf({
      title: t(`${stagesI18nKey}.HEADER`),
      context: exportContext.value,
      generatedAt: exportSummary.value,
      fileName: `embudo-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      summaryCards,
      stages: stagesTable.value,
      utmSources: utmSources.value,
      utmTotals: utmTotals.value,
      utmRevenue: utmRevenue.value,
      hasDealAmount: summary.has_deal_amount === true,
      totals: {
        contacts: summary.total_contacts,
        conversion: summary.conversion_rate,
        revenue: summary.total_revenue,
        avgWonTime: summary.avg_won_time_seconds,
        hasWonStage: Boolean(wonRow.value),
        conversionBySource: conversionBySource.value,
      },
      layers: buildFunnelLayersForPdf({
        stages: stagesTable.value,
        total: summary.total_contacts,
        totalLabel: t(`${stagesI18nKey}.TOTAL_LEADS_FUNNEL`),
      }),
      labels: {
        stage: t(`${stagesI18nKey}.TABLE_HEADER.STAGE`),
        total: t(`${stagesI18nKey}.TABLE_HEADER.CONTACTS`),
        share: t(`${stagesI18nKey}.TABLE_HEADER.COHORT_PERCENT`),
        timeToStage: t(`${stagesI18nKey}.TABLE_HEADER.TIME_TO_STAGE`),
        totalRow: t(`${stagesI18nKey}.TOTAL_ROW`),
        conversionRow: t(`${stagesI18nKey}.CONVERSION_ROW`),
        revenueRow: t(`${stagesI18nKey}.REVENUE_ROW`),
        tableSection: t(`${stagesI18nKey}.TABLE_TITLE`),
        funnelSection: t(`${stagesI18nKey}.FUNNEL_TITLE`),
        leads: t(`${stagesI18nKey}.LEADS_LABEL`),
      },
      formatDuration,
      formatMoney,
    });
  } catch {
    useAlert(t(`${stagesI18nKey}.EXPORT_ERROR`));
  } finally {
    isExporting.value = false;
  }
};

// ---------------------------------------------------------------------------
// PNG
//
// El embudo es un SVG propio: se serializa y se rasteriza a la escala que se
// quiera, asi que sale nitido. La tabla es DOM y no queda otra que pasar por
// html2canvas, que es un rasterizador aproximado.
// ---------------------------------------------------------------------------
const SVG_NS = 'http://www.w3.org/2000/svg';

const downloadDataUrl = (dataUrl, extension) => {
  const link = document.createElement('a');
  link.download = `embudo-${format(new Date(), 'yyyy-MM-dd')}.${extension}`;
  link.href = dataUrl;
  link.click();
};

const svgText = (x, y, content, { size, weight, fill }) => {
  const node = document.createElementNS(SVG_NS, 'text');
  node.setAttribute('x', String(x));
  node.setAttribute('y', String(y));
  node.setAttribute('font-size', String(size));
  node.setAttribute('font-weight', String(weight));
  node.setAttribute('fill', fill);
  node.textContent = content;
  return node;
};

const buildFunnelExportSvg = (funnelNode, colors) => {
  const box = funnelNode.getBoundingClientRect();
  const width = Math.max(1, Math.round(box.width));
  const height = Math.max(1, Math.round(box.height));
  const headerHeight = 86;

  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('xmlns', SVG_NS);
  root.setAttribute('width', String(width));
  root.setAttribute('height', String(height + headerHeight));
  root.setAttribute('viewBox', `0 0 ${width} ${height + headerHeight}`);
  // Al serializar, el SVG queda fuera del documento y el CSS de la pagina no lo
  // alcanza: sin font-family propio los textos del embudo saldrian en serif
  root.setAttribute(
    'font-family',
    'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif'
  );

  root.appendChild(
    svgText(24, 32, t(`${stagesI18nKey}.HEADER`), {
      size: 19,
      weight: 700,
      fill: colors.strong,
    })
  );
  root.appendChild(
    svgText(24, 54, exportContext.value, {
      size: 11.5,
      weight: 400,
      fill: colors.soft,
    })
  );
  root.appendChild(
    svgText(24, 74, exportSummary.value, {
      size: 12.5,
      weight: 600,
      fill: colors.muted,
    })
  );

  const group = document.createElementNS(SVG_NS, 'g');
  group.setAttribute('transform', `translate(0, ${headerHeight})`);
  const inner = funnelNode.cloneNode(true);
  inner.setAttribute('width', String(width));
  inner.setAttribute('height', String(height));
  group.appendChild(inner);
  root.appendChild(group);

  return { root, width, height: height + headerHeight };
};

const rasterizeSvg = async (svgRoot, width, height, scale, background) => {
  const source = new XMLSerializer().serializeToString(svgRoot);
  const image = new Image();
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
};

const captureDomToPngDataUrl = async (node, scale, background) => {
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(node, {
    backgroundColor: background,
    scale,
    useCORS: true,
    logging: false,
    // width/height medidos con las reglas de captura ya aplicadas, y nunca
    // windowWidth: pasarle un viewport falso es lo que producia los cortes
    width: node.scrollWidth,
    height: node.scrollHeight,
    scrollX: 0,
    scrollY: 0,
    onclone: clonedDocument => {
      clonedDocument.body.classList.add('capturing-report');
    },
  });
  return canvas.toDataURL('image/png');
};

const exportPng = async () => {
  if (!reportRef.value || isExporting.value) return;

  isExporting.value = true;
  try {
    const isDark = document.body.classList.contains('dark');
    const background = isDark ? '#111113' : '#ffffff';
    // Nitidez: se multiplica el DPR real, con techo para no generar un PNG enorme
    const scale = Math.min(4, Math.max(2, window.devicePixelRatio || 1) * 2);
    const colors = isDark
      ? { strong: '#EDEEF0', muted: '#B0B4BA', soft: '#8B8D98' }
      : { strong: '#1C2024', muted: '#60646C', soft: '#8B8D98' };

    const funnel = reportRef.value.querySelector('svg[data-export-svg]');
    let dataUrl;

    if (funnel) {
      const { root, width, height } = buildFunnelExportSvg(funnel, colors);
      dataUrl = await rasterizeSvg(root, width, height, scale, background);
    } else {
      // La medicion tiene que hacerse con el layout de captura ya aplicado en la
      // pagina real, si no el canvas queda de otro tamano que el contenido
      document.body.classList.add('capturing-report');
      await nextTick();
      await new Promise(resolve => {
        requestAnimationFrame(resolve);
      });
      try {
        dataUrl = await captureDomToPngDataUrl(
          reportRef.value,
          scale,
          background
        );
      } finally {
        document.body.classList.remove('capturing-report');
      }
    }

    downloadDataUrl(dataUrl, 'png');
  } catch {
    useAlert(t(`${stagesI18nKey}.EXPORT_ERROR`));
  } finally {
    isExporting.value = false;
  }
};

const handleExportAction = ({ value }) => {
  toggleExportDropdown(false);
  if (value === 'pdf') exportPdf();
  if (value === 'png') exportPng();
};

onMounted(() => {
  if (!teams.value?.length) store.dispatch('teams/get');
  if (!contactAttributeDefinitions.value?.length) {
    store.dispatch('attributes/get');
  }
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <OverviewKpiRow
      :cohort-summary="cohortSummary"
      :avg-first-response-time="
        accountConversationMetric?.avg_first_response_time || 0
      "
      :never-responded="neverRespondedCount"
      :attention="attention"
    />

    <!-- El embudo manda y ocupa la columna ancha; a su lado, el origen de
         los leads y el estado de la atención, que se leen de un vistazo. -->
    <div
      class="grid items-start grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <div ref="reportRef" class="w-full conversation-metric report-print-area">
        <MetricCard
          :header="t(`${stagesI18nKey}.HEADER`)"
          :is-loading="uiFlags.isFetchingAccountConversationMetric"
          :loading-message="t(`${stagesI18nKey}.LOADING_MESSAGE`)"
        >
          <template #header>
            <div class="col-span-full flex flex-col gap-2.5 w-full">
              <!-- Título h5 y Badge En Vivo -->
              <div class="flex items-center gap-2 flex-row">
                <h5 class="mb-0 text-n-slate-12 font-semibold text-base">
                  {{ t(`${stagesI18nKey}.HEADER`) }}
                </h5>
                <span
                  class="flex flex-row items-center py-0.5 px-2 rounded bg-n-teal-3 text-xs"
                >
                  <span
                    class="bg-n-teal-9 h-1 w-1 rounded-full mr-1 rtl:mr-0 rtl:ml-0"
                  />
                  <span class="text-xs text-n-teal-11">
                    {{ $t('OVERVIEW_REPORTS.LIVE') }}
                  </span>
                </span>
              </div>

              <!-- Solo aparece en el PDF y en el PNG: sin los filtros interactivos
                 la exportacion no diria de que periodo habla -->
              <p class="report-export-only text-xs text-n-slate-11 mb-0">
                {{ exportContext }}
              </p>

              <!-- Barra de filtros compacta -->
              <div class="flex items-center gap-1.5 flex-wrap w-full">
                <!-- Criterio, equipo y fuente: mismos chips que el resto -->
                <div
                  v-tooltip.top="selectedCriteriaTooltip"
                  class="z-50 report-export-hide"
                >
                  <FilterChip
                    :model-value="selectedCriteriaOption"
                    :options="criteriaOptions"
                    label-key="label"
                    track-key="value"
                    :prefix="t(`${stagesI18nKey}.FILTER_CRITERIA_LABEL`)"
                    icon="i-lucide-sliders-horizontal"
                    :clearable="false"
                    @update:model-value="onCriteriaChange"
                  />
                </div>

                <FilterChip
                  v-if="teams.length"
                  v-model="selectedTeam"
                  class="z-50 report-export-hide"
                  :options="teams"
                  :placeholder="t(`${stagesI18nKey}.FILTER_TEAM`)"
                  icon="i-lucide-users-round"
                />

                <FilterChip
                  v-for="attribute in syncedAttributes"
                  :key="attribute.id"
                  class="z-50 report-export-hide"
                  :model-value="attributeSelection(attribute)"
                  :options="attributeOptions(attribute)"
                  :placeholder="attribute.attributeDisplayName"
                  icon="i-lucide-tag"
                  @update:model-value="
                    option => setAttributeSelection(attribute, option)
                  "
                />

                <!-- Selector Único de Fechas (Rango con preajustes + personalizado) -->
                <HeatmapDateRangeSelector
                  v-model:from="selectedFrom"
                  v-model:to="selectedTo"
                  class="report-export-hide"
                  default-selection="last_30_days"
                  icon="i-lucide-calendar"
                  :prefix-label="t(`${stagesI18nKey}.DATE_FILTER_LABEL`)"
                />

                <!-- Desglose por fuentes UTM: se ve siempre que la cuenta
                     tenga fuentes. El desglose vive en la tabla, así que
                     encenderlo lleva a esa vista. -->
                <button
                  v-if="utmSources.length"
                  type="button"
                  role="switch"
                  :aria-checked="showUtmBreakdown"
                  class="inline-flex items-center h-8 gap-2 px-2.5 text-xs font-medium transition-colors border rounded-lg cursor-pointer select-none focus:outline-hidden report-export-hide"
                  :class="[
                    showUtmBreakdown
                      ? 'bg-n-blue-3 border-n-blue-7 text-n-blue-11'
                      : 'bg-n-solid-1 hover:bg-n-alpha-1 border-n-container text-n-slate-11',
                  ]"
                  @click="toggleUtmBreakdown"
                >
                  <span
                    class="i-lucide-columns-3 size-3.5 flex-shrink-0"
                    :class="
                      showUtmBreakdown ? 'text-n-brand' : 'text-n-slate-10'
                    "
                  />
                  <span>{{ t(`${stagesI18nKey}.UTM_BREAKDOWN_TOGGLE`) }}</span>
                  <span
                    class="relative h-4 w-7 rounded-full transition-colors flex-shrink-0"
                    :class="showUtmBreakdown ? 'bg-n-brand' : 'bg-n-slate-6'"
                  >
                    <span
                      class="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-n-background shadow-sm transition-transform"
                      :class="
                        showUtmBreakdown ? 'translate-x-3' : 'translate-x-0'
                      "
                    />
                  </span>
                </button>

                <!-- Exportar: PDF por impresion del navegador, o PNG del bloque -->
                <div
                  v-on-clickaway="() => toggleExportDropdown(false)"
                  class="relative flex items-center z-50 report-export-hide"
                >
                  <button
                    type="button"
                    class="inline-flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium transition-colors border rounded-lg cursor-pointer border-n-container bg-n-solid-1 hover:bg-n-alpha-1 text-n-slate-12 focus:outline-hidden disabled:opacity-60"
                    :disabled="isExporting"
                    @click="toggleExportDropdown()"
                  >
                    <span
                      class="size-3.5 flex-shrink-0 text-n-slate-10"
                      :class="
                        isExporting
                          ? 'i-lucide-loader-circle animate-spin'
                          : 'i-lucide-download'
                      "
                    />
                    <span>{{ t(`${stagesI18nKey}.EXPORT_LABEL`) }}</span>
                    <span
                      class="i-lucide-chevron-down size-3 text-n-slate-10"
                    />
                  </button>
                  <DropdownMenu
                    v-if="showExportDropdown"
                    :menu-items="exportOptions"
                    class="mt-1 ltr:right-0 rtl:left-0 top-full min-w-[190px]"
                    @action="handleExportAction($event)"
                  />
                </div>

                <!-- Conmutador de vista: Tabla / Embudo -->
                <div
                  class="inline-flex items-center h-8 p-0.5 border rounded-lg ltr:ml-auto rtl:mr-auto border-n-container bg-n-alpha-2 report-export-hide"
                >
                  <button
                    v-for="option in viewOptions"
                    :key="option.value"
                    type="button"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer"
                    :class="
                      activeView === option.value
                        ? 'bg-n-solid-1 text-n-slate-12 shadow-2xs'
                        : 'text-n-slate-10 hover:text-n-slate-12'
                    "
                    @click="activeView = option.value"
                  >
                    <span class="size-3.5" :class="[option.icon]" />
                    <span>{{ option.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Tabla Completa de Estados de Contacto y Embudo -->
          <ContactStagesTable
            :stages-table="stagesTable"
            :cohort-summary="cohortSummary"
            :filter-params="currentFilterParams"
            :is-loading="uiFlags.isFetchingAccountConversationMetric"
            :show-utm-breakdown="showUtmBreakdown"
            :utm-sources="utmSources"
            :utm-totals="utmTotals"
            :utm-revenue="utmRevenue"
            :active-view="activeView"
          />
        </MetricCard>
      </div>

      <div class="flex flex-col gap-4">
        <LeadSourcesCard
          :sources="utmSources"
          :totals="utmTotals"
          :won-counts="wonRow?.utm_counts || {}"
        />
        <AttentionCard
          :attention="attention"
          :avg-closing-time="cohortSummary.avg_won_time_seconds || 0"
        />
      </div>
    </div>
  </div>
</template>

<!-- Sin scope a proposito: las reglas alcanzan al clon interno que arma
     html2canvas, que vive fuera del arbol de este componente -->
<style>
/* ---------------------------------------------------------------------------
   CAPTURA PNG (html2canvas, solo la vista tabla)

   html2canvas reimplementa el motor de render y hay CSS moderno que no
   soporta. Sin estas reglas la imagen sale con cuadrados negros y texto
   cortado: los iconos i-lucide-* se dibujan con mask-image (los pinta como
   cajas) y mide mal la caja de linea (truncate corta el texto).
   El embudo no pasa por aca: se serializa desde su propio SVG.
   El PDF tampoco: se genera desde los datos con jsPDF.
--------------------------------------------------------------------------- */
.report-export-only {
  display: none;
}

body.capturing-report .report-export-only {
  display: block !important;
}

body.capturing-report .report-export-hide {
  display: none !important;
}

/* La matriz UTM vive dentro de un contenedor con scroll horizontal: si no se
   libera, el PNG sale recortado en la primera columna que no entra */
body.capturing-report .report-print-area .overflow-x-auto {
  overflow: visible !important;
}

body.capturing-report .report-print-area [class*='i-lucide-'] {
  display: none !important;
}

body.capturing-report .report-print-area,
body.capturing-report .report-print-area *:not(svg):not(svg *) {
  overflow: visible !important;
  text-overflow: clip !important;
  line-height: 1.65 !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  filter: none !important;
  letter-spacing: normal !important;
}

body.capturing-report .report-print-area td,
body.capturing-report .report-print-area th,
body.capturing-report .report-print-area p,
body.capturing-report .report-print-area h3,
body.capturing-report .report-print-area h4,
body.capturing-report .report-print-area h5,
body.capturing-report .report-print-area span {
  height: auto !important;
  min-height: 0 !important;
  padding-bottom: 2px !important;
}
</style>
